import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {subDays, subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import {useQuery} from "@tanstack/react-query";
import equipmentsService from "../../../services/web/equipments-service.js";
import equipmentsInUseService from "../../../services/reports/equipments-in-use-service.js";
import reportUtils from "../../../utils/report-utils.js";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";

const initialFilters = {
  dtStart: subDays(new Date(),7),
  dtEnd: new Date(),
  equipments: [],
  searchDetails: false,
};

const filterLabels = {
  dtStart: "Data/hora inicial",
  dtEnd: "Data/hora final",
  equipments: "Equipamento",
  searchDetails: "Exibir detalhes"
};

const EquipmentsInUse = () => {
  const [loading, setLoading] = useState(false);

  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(null);
  const [filtersToAudit, setFiltersToAudit] = useState(null);

  const [filters, setFilters] = useState(initialFilters);
  const [reportData, setReportData] = useState(null);

  const [details, setDetails] = useState(false);

  const [title] = useState("Equipamentos em uso");
  const [headerSelected, setHeaderSelected] = useState(-1);
  const printComponentRef = useRef(null);

  const {data: equipments = [], isLoading: isLoadingEquipments} = useQuery({
    queryFn: equipmentsService.allEquipments,
    select: data => data.map(equipment => ({label: equipment.text, value: equipment.id})),
    queryKey: ['equipments']
  })

  const apply = async () => {
    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null)
      }
    });

    const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

    if (!periodValidation.ok) {
      ReportUtils.alertErrorMessage(periodValidation.message);
      setLoading(false);
      return;
    }

    try {
      setLoading(true)
      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
        searchDetails: filters.searchDetails ? "Sim" : "Não",
      };

      const auditFilters = ReportUtils.filters({
          ...filters,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
          searchDetails: formattedFilters.searchDetails,
        },
        filterLabels, true);

      const data = await equipmentsInUseService.apply({...buildPayload(filters), filters: auditFilters});
      setDetails(filters.searchDetails)
      setFiltersToAudit(auditFilters)

      setReportData(data);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {

      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    const refDate = new Date;
    setFilters({...initialFilters,
                      dtStart: subDays(refDate, 7),
                      dtEnd: refDate});
    setReportData(null);
  }

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  function buildPayload(data) {
    const payloadObj = {
      dtStart: reportUtils.formatDateTime(data.dtStart),
      dtEnd: reportUtils.formatDateTime(data.dtEnd),
    }

    if(data.detailsSearch) payloadObj.detailsSearch = "on"
    if(data.equipments.length > 0) payloadObj.equipments = data.equipments.map(el => el.value)

    return payloadObj;
  }

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
        searchDetails: filters.searchDetails ? "Sim" : "Não"
      }, filterLabels)
    );

  }, [reportData]);

  return (
    <div className={"flex"}>

      <Template isLoading={loading || isLoadingEquipments}>
        <Template.Header>
          <div className="flex justify-between w-full">
            <Template.Title>{title}</Template.Title>
            <HeaderButtons
              exportable={!details}
              exportInSheets={true}
              printable={true}
              headerChange={true}
              printComponentRef={printComponentRef}
              reportData={reportData}
              data={reportData?.length}
              headerSelected={headerSelected}
              setHeaderSelected={setHeaderSelected}
            />
          </div>
        </Template.Header>
        <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
          <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

            <Filters setFilters={setFilters} filters={filters} equipments={equipments}/>
            <AutoDatetime setFilters={setFilters}/>

          </Menu>
        </Template.Body>
      </Template>

      <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
        <Card.Body>
          <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
            <NotAppliedContent reportData={reportData}/>
            <NoDataContent reportData={reportData} data={reportData?.length}/>
            <Content filters={filtersToAudit} reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} details={details}/>
          </div>
        </Card.Body>
      </Card>

    </div>
  );
};

export default EquipmentsInUse;
